import { dateLabel, dowShort, dayNum } from "../lib/venueDate";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

/**
 * Material date selector — a horizontally scrollable row of venue-local date
 * pills (weekday + day-of-month), the active day highlighted lime. Selecting a
 * pill switches the day; the prev/next chevrons step one day (and, at an edge,
 * scroll the window forward/back). The cart persists across days — selections on
 * other days stay in the tray. Every label reads in venue-local terms.
 *
 * Presentation only: the parent owns the date state + the `days` window and the
 * real switching handlers; this component just renders the controls.
 */
export function SheetDateNav({
  date,
  days,
  timezoneLabel,
  onPrev,
  onNext,
  onSelect,
}: {
  /** The selected venue-local date key (YYYY-MM-DD). */
  date: string;
  /** The window of date keys to render as pills, chronological. */
  days: string[];
  timezoneLabel?: string | null;
  onPrev: () => void;
  onNext: () => void;
  onSelect: (dateKey: string) => void;
}) {
  return (
    <Stack spacing={0.75} role="group" aria-label="Court sheet date">
      <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
        <IconButton type="button" onClick={onPrev} aria-label="Previous day">
          <ChevronLeftRounded />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            overflowX: "auto",
            flex: 1,
            py: 0.5,
          }}
        >
          {days.map((d) => {
            const active = d === date;
            return (
              <Button
                key={d}
                type="button"
                variant={active ? "contained" : "outlined"}
                aria-pressed={active}
                aria-current={active ? "date" : undefined}
                aria-label={dateLabel(d)}
                onClick={() => onSelect(d)}
                sx={{ minWidth: 48, px: 0.5, py: 0.5, flexDirection: "column", gap: 0, borderRadius: "12px", lineHeight: 1.1 }}
              >
                <Typography component="span" sx={{ fontSize: "0.625rem", fontWeight: 800, textTransform: "uppercase" }}>
                  {dowShort(d)}
                </Typography>
                <Typography component="span" sx={{ fontWeight: 800, fontSize: "1rem" }}>
                  {dayNum(d)}
                </Typography>
              </Button>
            );
          })}
        </Box>

        <IconButton type="button" onClick={onNext} aria-label="Next day">
          <ChevronRightRounded />
        </IconButton>
      </Stack>

      {timezoneLabel ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: "right" }}
        >
          {timezoneLabel}
        </Typography>
      ) : null}
    </Stack>
  );
}
